import { ProductV2, formatPriceText } from '@common'
import cx from 'classnames'
import Image from 'next/image'

import PassCardBg from '@/assets/images/storefronts/brawlstars/pass-card-bg.png'
import { useLocale } from '@/features/locale/useLocale'
import { BuyButton } from '@/features/storefronts/_storeBase/BuyButton/BuyButton'
import { CardBase } from '@/features/storefronts/_storeBase/card/CardBase'
import { useHandleClick } from '@/features/storefronts/_storeBase/card/useHandleClick'
import { ProductRibbon } from '@/features/storefronts/_storeBase/cardRibbon/ProductRibbon'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { useTranslatable } from '@/localization/useTranslatable'
import { resolveProductImage } from '@/utils/resolveProductImage'
import { useSnowplowProductCardViewImpression } from '@/utils/snowplow'
import { useAppSelector } from '@/utils/useAppSelector'

import { StoreTypography } from '../../_storeBase/typography/StoreTypography'

import styles from './PassCardBrawl.module.scss'

interface PassCardProps {
  product: ProductV2
  productPage?: boolean
}

export const PassCardBrawl: React.FC<PassCardProps> = ({ product, productPage = false }) => {
  const translatable = useTranslatable()
  const locale = useLocale()
  const market = useAppSelector((state) => state.config.market)
  const gameSlug = useGameSlug()
  const pathname = `/game/${gameSlug}/product/${product.slug}`

  const currency = market?.currencyCode
  const currencyCountry = market?.currencyCountry
  const price = currencyCountry && formatPriceText(product.prices[currencyCountry] ?? 0, locale, currency)
  const isSeasonPass = !!product.contentCounts.SEASON_PASS

  const { handleClick } = useHandleClick({ product, pathname })

  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    product,
    'PassCardBrawl',
    'PassCardBrawl',
  )

  return isSeasonPass ? (
    <CardBase
      href={pathname}
      className={cx(styles.PassCard, { [styles.productPage]: productPage })}
      onClick={handleClick}
    >
      <ProductRibbon product={product} className={styles.ribbon} />

      <Image
        src={PassCardBg}
        alt="passcard-background"
        fill
        quality={100}
        sizes="360px"
        style={{ objectFit: 'cover' }}
      />
      <div className={styles.header} ref={snowplowViewImpressionRef}>
        <div className={styles.passContainer}>
          <Image
            className={styles.passImage}
            src={resolveProductImage(product)}
            fill
            sizes="360px"
            alt="passcard-background"
            placeholder="empty"
          />
        </div>
        <StoreTypography tag="h3" textSize="lg" style="outlined" className={styles.Title}>
          {translatable(product.title, 'title')}
        </StoreTypography>
      </div>

      <div className={styles.Footer}>
        <div />
        <BuyButton additionalClasses={styles.Button} type="Secondary" content={price} product={product} />
      </div>
    </CardBase>
  ) : null
}
